import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useApiToken } from './useApiToken'
import { alertRulesApi } from '../lib/api/alertRules'

const RULES_KEY = ['alert-rules'] as const

export function useAlertRules() {
  const getToken = useApiToken()
  return useQuery({
    queryKey: RULES_KEY,
    queryFn: async () => {
      const token = await getToken()
      return alertRulesApi.list(token)
    },
  })
}

export function useCreateAlertRule() {
  const getToken = useApiToken()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: object) => {
      const token = await getToken()
      return alertRulesApi.create(token, data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
    onError: (e: Error) => toast.error('Failed to create rule: ' + e.message),
  })
}

export function useUpdateAlertRule() {
  const getToken = useApiToken()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: object }) => {
      const token = await getToken()
      return alertRulesApi.update(token, id, data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
    onError: (e: Error) => toast.error('Failed to update rule: ' + e.message),
  })
}

export function useDeleteAlertRule() {
  const getToken = useApiToken()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const token = await getToken()
      return alertRulesApi.delete(token, id)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES_KEY }),
    onError: (e: Error) => toast.error('Failed to delete rule: ' + e.message),
  })
}

export function useTestAlertRule() {
  const getToken = useApiToken()
  return useMutation({
    mutationFn: async (id: number) => {
      const token = await getToken()
      return alertRulesApi.test(token, id)
    },
  })
}
